// Licensed to the Apache Software Foundation (ASF) under one
// or more contributor license agreements.  See the NOTICE file
// distributed with this work for additional information
// regarding copyright ownership.  The ASF licenses this file
// to you under the Apache License, Version 2.0 (the
// "License"); you may not use this file except in compliance
// with the License.  You may obtain a copy of the License at
//
//   http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing,
// software distributed under the License is distributed on an
// "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
// KIND, either express or implied.  See the License for the
// specific language governing permissions and limitations
// under the License.

import axios from 'axios'
import { vueProps } from '@/vue-app'
import router from '@/router'
import { VueAxios } from '@/utils/axios'
import notification from 'ant-design-vue/es/notification'
import { CURRENT_PROJECT } from '@/store/mutation-types'
import { i18n } from '@/locales'
import store from '@/store'
import authService, { AUTH_CONFIG } from '@/customizations/utils/authService'
import allowListService from '@/customizations/utils/allowListService'

const ALLOWED_401_COMMANDS = [
  'forgotPassword',
  'listIdps',
  'cloudianIsEnabled',
  'listOauthProvider',
  'readyForShutdown'
]

let source
let lastTimeoutLogout = 0

const service = axios.create({
  timeout: AUTH_CONFIG.REQUEST_TIMEOUT
})

const err = (error) => {
  const response = error.response
  let countNotify = store.getters.countNotify

  if (response && response.status) {
    if (response.status === 531 || response.status === 432) {
      return Promise.reject(error)
    }

    if (response.status === 429) {
      if (!authService.logoutInProgress) {
        authService.performLogout('rate-limit')
      }
      return Promise.reject(error)
    }

    if (response.status === 401) {
      if (response.config?.params?.command &&
          ALLOWED_401_COMMANDS.includes(response.config.params.command)) {
        return Promise.reject(error)
      }

      const now = Date.now()

      if (authService.logoutInProgress || (now - lastTimeoutLogout) < AUTH_CONFIG.DEBOUNCE_TIMEOUT) {
        return
      }

      lastTimeoutLogout = now
      const originalPath = router.currentRoute.value?.path || '/'

      const errorData = response.data
      for (const key in errorData) {
        if (key.includes('response') && errorData[key]?.errortext?.includes('not available for user')) {
          countNotify++
          store.commit('SET_COUNT_NOTIFY', countNotify)
          notification.error({
            top: '65px',
            message: 'Error',
            description: errorData[key].errortext + ' ' + i18n.global.t('error.unable.to.proceed'),
            duration: 0,
            onClose: () => {
              let countNotify = store.getters.countNotify
              countNotify > 0 ? countNotify-- : countNotify = 0
              store.commit('SET_COUNT_NOTIFY', countNotify)
            }
          })
          return
        }
      }

      countNotify++
      store.commit('SET_COUNT_NOTIFY', countNotify)

      if (source) {
        source.cancel('Sessão expirada')
        source = null
      }

      if (originalPath === '/verify2FA' || originalPath === '/setup2FA') {
        notification.error({
          top: '65px',
          message: i18n.global.t('label.2FA'),
          description: i18n.global.t('message.error.verifying.2fa'),
          key: 'http-401',
          duration: 0,
          onClose: () => {
            let countNotify = store.getters.countNotify
            countNotify > 0 ? countNotify-- : countNotify = 0
            store.commit('SET_COUNT_NOTIFY', countNotify)
          }
        })
      } else {
        notification.error({
          top: '65px',
          message: i18n.global.t('label.error'),
          description: i18n.global.t('message.session.expired'),
          duration: 3000,
          onClose: () => {
            let countNotify = store.getters.countNotify
            countNotify > 0 ? countNotify-- : countNotify = 0
            store.commit('SET_COUNT_NOTIFY', countNotify)
          }
        })
      }

      authService.performLogout('timeout').then(() => {
        if (originalPath !== '/user/login') {
          setTimeout(() => {
            router.push({ path: '/user/login', query: { redirect: originalPath } })
          }, AUTH_CONFIG.REDIRECT_DELAY)
        }
      }).catch(() => {
        setTimeout(() => {
          router.push({ path: '/user/login' })
        }, AUTH_CONFIG.REDIRECT_DELAY)
      })
    }

    if (response.status === 403) {
      const data = response.data
      countNotify++
      store.commit('SET_COUNT_NOTIFY', countNotify)
      notification.error({
        top: '65px',
        message: i18n.global.t('label.forbidden'),
        description: data.message,
        onClose: () => {
          let countNotify = store.getters.countNotify
          countNotify > 0 ? countNotify-- : countNotify = 0
          store.commit('SET_COUNT_NOTIFY', countNotify)
        }
      })
    }

    if (response.status === 500) {
      countNotify++
      store.commit('SET_COUNT_NOTIFY', countNotify)
      notification.error({
        top: '65px',
        message: i18n.global.t('label.error'),
        description: response.data.message || i18n.global.t('message.request.failed'),
        onClose: () => {
          let countNotify = store.getters.countNotify
          countNotify > 0 ? countNotify-- : countNotify = 0
          store.commit('SET_COUNT_NOTIFY', countNotify)
        }
      })
    }

    if (response.status === 404) {
      countNotify++
      store.commit('SET_COUNT_NOTIFY', countNotify)
      notification.error({
        top: '65px',
        message: i18n.global.t('label.not.found'),
        description: i18n.global.t('message.resource.not.found'),
        onClose: () => {
          let countNotify = store.getters.countNotify
          countNotify > 0 ? countNotify-- : countNotify = 0
          store.commit('SET_COUNT_NOTIFY', countNotify)
        }
      })
      router.push({ path: '/exception/404' })
    }

    if (response.status >= 504) {
      countNotify++
      store.commit('SET_COUNT_NOTIFY', countNotify)
      notification.error({
        top: '65px',
        message: i18n.global.t('label.error'),
        description: i18n.global.t('message.network.error'),
        onClose: () => {
          let countNotify = store.getters.countNotify
          countNotify > 0 ? countNotify-- : countNotify = 0
          store.commit('SET_COUNT_NOTIFY', countNotify)
        }
      })
    }
  }

  if (error.isAxiosError && !error.response) {
    countNotify++
    store.commit('SET_COUNT_NOTIFY', countNotify)
    notification.warn({
      top: '65px',
      message: error.message || i18n.global.t('message.network.error'),
      description: i18n.global.t('message.network.error.description'),
      key: 'network-error',
      onClose: () => {
        let countNotify = store.getters.countNotify
        countNotify > 0 ? countNotify-- : countNotify = 0
        store.commit('SET_COUNT_NOTIFY', countNotify)
      }
    })
  }

  return Promise.reject(error)
}

service.interceptors.request.use(config => {
  source = sourceToken.getSource()
  config.cancelToken = source.token

  handleGetRequestParams(config)

  handlePostRequestParams(config)

  return config
}, err)

function handleGetRequestParams (config) {
  if (config && config.params) {
    config.params.response = 'json'
    const project = vueProps.$localStorage.get(CURRENT_PROJECT)
    if (!config.params.projectid && !config.params.ignoreproject && project && project.id) {
      if (config.params.command === 'listTags') {
        config.params.projectid = '-1'
      } else if (config.params.command !== 'assignVirtualMachine') {
        config.params.projectid = project.id
      }
    }
    if (config.params.ignoreproject !== undefined) {
      delete config.params.ignoreproject
    }
  }
}

function handlePostRequestParams (config) {
  if (config && config.data && config.data instanceof URLSearchParams) {
    const project = vueProps.$localStorage.get(CURRENT_PROJECT)
    const command = config.data.get('command')
    const hasProjectId = config.data.has('projectid')
    const ignoreProject = config.data.has('ignoreproject')

    if (!hasProjectId && !ignoreProject && project && project.id) {
      if (command === 'listTags') {
        config.data.append('projectid', '-1')
      } else if (command !== 'assignVirtualMachine') {
        config.data.append('projectid', project.id)
      }
    }
    if (config.data.has('ignoreproject')) {
      config.data.delete('ignoreproject')
    }
  }
}

const filterDescriptions = (data) => {
  if (!data) return data

  const processObject = (obj) => {
    if (!obj || typeof obj !== 'object') return obj

    if (Array.isArray(obj)) {
      return obj.map(item => processObject(item))
    }

    const result = { ...obj }
    for (const key in result) {
      if (key === 'description' && typeof result[key] === 'string') {
        result[key] = result[key]
          .replace(/an Apache CloudStack/gi, 'a King Cloud')
          .replace(/a Apache CloudStack/gi, 'a King Cloud')
          .replace(/Apache CloudStack/gi, 'King Cloud')
          .replace(/CloudStack/gi, 'King Cloud')
          .replace(/cloudstack/gi, 'King Cloud')
      } else if (typeof result[key] === 'object') {
        result[key] = processObject(result[key])
      }
    }
    return result
  }

  for (const key in data) {
    if (key.toLowerCase().includes('response')) {
      data[key] = processObject(data[key])
    }
  }

  return data
}

service.interceptors.response.use((response) => {
  response.data = allowListService.filterApiResponse(response.data)
  response.data = filterDescriptions(response.data)
  return response.data
}, err)

const installer = {
  vm: {},
  install (app) {
    app.use(VueAxios, service)
  }
}

const sourceToken = {
  init: () => { source = axios.CancelToken.source() },
  isCancel: (e) => {
    return axios.isCancel(e)
  },
  checkExistSource: () => {
    return source
  },
  getSource: () => {
    if (!source) sourceToken.init()
    return source
  },
  cancel: () => {
    if (!source) sourceToken.init()
    if (source) {
      source.cancel()
      source = null
    }
  }
}

export {
  installer as VueAxios,
  service as axios,
  sourceToken,
  filterDescriptions,
  allowListService
}
